import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api } from "./api"

// Locations data layer for the Console. Reusable venues each agenda item can
// reference; self-referencing `parentLocationId` nests rooms/pools/stages
// inside the hotel. Per-event scoped.
//
// Writes flow through the validated custom actions (auto-CRUD is disabled on
// the table) so the parent-cycle + per-event parent checks can't be bypassed:
//   POST /locations/create · /locations/update · /locations/delete
//   GET  /locations          (list, auto-CRUD read)
//   POST /locations/get-detail (HQ bundle: row + parentChain + children + items)

// ── Types (mirror quickback/features/locations) ─────────────────────────────
export type LocationRow = {
  id: string
  eventId: string
  parentLocationId: string | null
  name: string
  notes: string | null
  floorPlanUrl: string | null
  squareFeet: number | null
  lengthFeet: number | null
  widthFeet: number | null
  heightFeet: number | null
  position: number
  streetAddress: string | null
  addressLocality: string | null
  addressRegion: string | null
  postalCode: string | null
  addressCountry: string | null
  lat: number | null
  lon: number | null
}

export type AgendaItemLite = {
  id: string
  locationId: string | null
}

export type LocationDetailItem = {
  id: string
  name: string
  startsAt: string | null
  endsAt: string | null
  accessMode: string | null
  isRequired: number | boolean | null
  rsvpMode: string | null
}

export type LocationSetup = {
  id: string
  eventId: string
  organizationId: string
  locationId: string
  name: string
  capacity: number | null
  pax: string | null
  notes: string | null
  position: number
}

export type LocationDetail = {
  success: true
  location: LocationRow
  parentChain: Array<{ id: string; name: string; parentLocationId: string | null }>
  children: Array<{ id: string; name: string; parentLocationId: string | null; position: number }>
  setups: LocationSetup[]
  agendaItems: LocationDetailItem[]
}

type ListEnvelope<T> = { data: T[]; meta?: { total?: number } }

// ── query keys ───────────────────────────────────────────────────────────────
// `["resource", <table>, <filters>]` matches the vendored realtime layer so it
// surgically patches the list cache on live CRUD events (table has realtime on).
export const locationsKey = (eventId: string) =>
  ["resource", "locations", { eventId }] as const
export const locationDetailKey = (id: string) =>
  ["location-detail", id] as const
export const agendaItemLocationsKey = (eventId: string) =>
  ["resource", "agenda-items", { eventId, mode: "location-count" }] as const

// ── query options ────────────────────────────────────────────────────────────
export function locationsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: locationsKey(eventId),
    queryFn: () =>
      api<ListEnvelope<LocationRow>>("/api/v1/locations", {
        searchParams: { limit: 500, eventId, sort: "name", order: "asc" },
      }),
    select: (r) => r.data ?? [],
  })
}

export function agendaItemLocationsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: agendaItemLocationsKey(eventId),
    queryFn: () =>
      api<ListEnvelope<AgendaItemLite>>("/api/v1/agenda-items", {
        searchParams: { limit: 1000, eventId },
      }),
    select: (r) => r.data ?? [],
  })
}

export function locationDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: locationDetailKey(id),
    queryFn: () =>
      api<LocationDetail>("/api/v1/locations/get-detail", {
        method: "POST",
        body: { id },
      }),
  })
}

// ── mutations (ActionError-aware via `api`; callers toast on reject) ──────────
export type CreateLocationInput = {
  eventId: string
  name: string
  parentLocationId?: string | null
  notes?: string | null
  floorPlanUrl?: string | null
  squareFeet?: number | null
  lengthFeet?: number | null
  widthFeet?: number | null
  heightFeet?: number | null
  streetAddress?: string | null
  addressLocality?: string | null
  addressRegion?: string | null
  postalCode?: string | null
  addressCountry?: string | null
  lat?: number | null
  lon?: number | null
}

export async function createLocation(
  input: CreateLocationInput,
): Promise<{ success: true; location: LocationRow }> {
  return api("/api/v1/locations/create", { method: "POST", body: clean(input) })
}

export type UpdateLocationInput = {
  id: string
  name?: string
  parentLocationId?: string | null
  notes?: string | null
  floorPlanUrl?: string | null
  squareFeet?: number | null
  lengthFeet?: number | null
  widthFeet?: number | null
  heightFeet?: number | null
  streetAddress?: string | null
  addressLocality?: string | null
  addressRegion?: string | null
  postalCode?: string | null
  addressCountry?: string | null
  lat?: number | null
  lon?: number | null
}

export async function updateLocation(
  input: UpdateLocationInput,
): Promise<{ success: true; location: LocationRow; unchanged?: boolean }> {
  return api("/api/v1/locations/update", { method: "POST", body: clean(input) })
}

export async function deleteLocation(
  id: string,
): Promise<{ success: true; deleted: string }> {
  return api("/api/v1/locations/delete", { method: "POST", body: { id } })
}

export type UpsertLocationSetupInput = {
  id?: string
  locationId: string
  name: string
  capacity?: number | null
  pax?: string | null
  notes?: string | null
  position?: number
}

export async function upsertLocationSetup(
  input: UpsertLocationSetupInput,
): Promise<{ success: true; setup: LocationSetup }> {
  return api("/api/v1/locations/setups/upsert", { method: "POST", body: clean(input) })
}

export async function deleteLocationSetup(
  id: string,
): Promise<{ success: true; deleted: string }> {
  return api("/api/v1/locations/setups/delete", { method: "POST", body: { id } })
}

// Drop keys whose value is `undefined` so we never send `undefined` over the
// wire (would serialize to null and clobber). `null` is meaningful (clear) and
// is preserved.
function clean<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v
  }
  return out as Partial<T>
}

// ── cache helpers ────────────────────────────────────────────────────────────
// Splice an updated/created row into the list cache (the realtime-patched key)
// so the tree reflects the change without a refetch.
export function patchLocationInCache(
  qc: QueryClient,
  eventId: string,
  location: LocationRow,
): void {
  qc.setQueryData<ListEnvelope<LocationRow>>(locationsKey(eventId), (old) => {
    if (!old) return { data: [location] }
    const idx = old.data.findIndex((l) => l.id === location.id)
    if (idx === -1) return { ...old, data: [...old.data, location] }
    const next = old.data.slice()
    next[idx] = location
    return { ...old, data: next }
  })
}

export function removeLocationFromCache(
  qc: QueryClient,
  eventId: string,
  id: string,
): void {
  qc.setQueryData<ListEnvelope<LocationRow>>(locationsKey(eventId), (old) => {
    if (!old) return old
    return { ...old, data: old.data.filter((l) => l.id !== id) }
  })
}

// ── tree helpers ─────────────────────────────────────────────────────────────
export type FlatNode = { loc: LocationRow; depth: number }

// Build a flattened, depth-tagged render list. Roots = no parent (or a parent
// that's out of scope — defensive against stale parentLocationId). Siblings
// sort by position then name. Cycle-guarded.
export function buildFlatTree(locs: LocationRow[]): FlatNode[] {
  const byId = new Map<string, LocationRow>()
  for (const l of locs) byId.set(l.id, l)
  const childrenByParent = new Map<string, LocationRow[]>()
  const roots: LocationRow[] = []
  for (const l of locs) {
    if (l.parentLocationId == null || !byId.has(l.parentLocationId)) {
      roots.push(l)
    } else {
      const arr = childrenByParent.get(l.parentLocationId) ?? []
      arr.push(l)
      childrenByParent.set(l.parentLocationId, arr)
    }
  }
  const order = (a: LocationRow, b: LocationRow) =>
    (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name)
  roots.sort(order)
  for (const arr of childrenByParent.values()) arr.sort(order)

  const out: FlatNode[] = []
  const seen = new Set<string>()
  const walk = (loc: LocationRow, depth: number) => {
    if (seen.has(loc.id)) return
    seen.add(loc.id)
    out.push({ loc, depth })
    for (const child of childrenByParent.get(loc.id) ?? []) walk(child, depth + 1)
  }
  for (const root of roots) walk(root, 0)
  return out
}

// Descendant ids of `id` (excluding id itself) — for the parent picker, so a
// location can't be reparented under its own descendant (would cycle).
export function descendantIds(locs: LocationRow[], id: string): Set<string> {
  const childrenByParent = new Map<string, LocationRow[]>()
  for (const l of locs) {
    if (l.parentLocationId != null) {
      const arr = childrenByParent.get(l.parentLocationId) ?? []
      arr.push(l)
      childrenByParent.set(l.parentLocationId, arr)
    }
  }
  const out = new Set<string>()
  const stack = [...(childrenByParent.get(id) ?? [])]
  while (stack.length) {
    const cur = stack.pop()!
    if (out.has(cur.id)) continue
    out.add(cur.id)
    stack.push(...(childrenByParent.get(cur.id) ?? []))
  }
  return out
}

export function addressLines(loc: LocationRow): string[] {
  const out: string[] = []
  if (loc.streetAddress) out.push(loc.streetAddress)
  const cityLine = [loc.addressLocality, loc.addressRegion, loc.postalCode]
    .filter(Boolean)
    .join(", ")
  if (cityLine) out.push(cityLine)
  if (loc.addressCountry) out.push(loc.addressCountry)
  return out
}
